/**
 * Rôles et permissions — source unique pour l'API (contrôle) et la PWA
 * (affichage). L'API ne fait JAMAIS confiance au client : l'interface masque,
 * le serveur refuse.
 *
 * Deux axes indépendants :
 *  - le RÔLE dit ce qu'on peut faire (permissions) ;
 *  - le PÉRIMÈTRE dit sur quelles boutiques (voir `access` côté API).
 * OWNER et ADMIN voient tout le réseau ; les autres rôles ne voient que les
 * boutiques de leurs comptes Hiboutik et les boutiques qui leur sont ouvertes.
 */

export const ROLES = ["OWNER", "ADMIN", "MANAGER", "STAFF", "VIEWER"] as const;
export type Role = (typeof ROLES)[number];

export const ROLE_LABELS: Record<Role, string> = {
  OWNER: "Propriétaire",
  ADMIN: "Tête de réseau",
  MANAGER: "Gérant",
  STAFF: "Équipier",
  VIEWER: "Lecture seule",
};

export const ROLE_HIERARCHY: Record<Role, number> = {
  OWNER: 100,
  ADMIN: 80,
  MANAGER: 60,
  STAFF: 40,
  VIEWER: 20,
};

/** Rôles qui voient l'ensemble du réseau, sans grant de périmètre. */
export const NETWORK_WIDE_ROLES: readonly Role[] = ["OWNER", "ADMIN"];

export const PERMISSIONS = [
  "dashboard.read",
  "sales.read",
  "sales.customers", // données client des ventes (nom, contact)
  "stock.read",
  "stock.promo", // appliquer une remise dans Hiboutik
  "arrivals.read",
  "boutiques.manage", // créer / modifier / supprimer, clés Hiboutik
  "network.customize", // nom et logo du réseau
  "sync.run",
  "messages.read",
  "messages.send",
  "announcements.send",
  "tasks.create",
  "tasks.complete",
  "tasks.validate",
  "campaigns.read",
  "campaigns.manage",
  "members.read",
  "members.manage",
  "reports.manage",
  "planning.read",
  "planning.manage",
  "it.manage", // page IT : suivi des livraisons (si le réseau l'a activée)
] as const;
export type Permission = (typeof PERMISSIONS)[number];

export const PERMISSION_LABELS: Record<Permission, string> = {
  "dashboard.read": "Voir le tableau de bord",
  "sales.read": "Voir les ventes",
  "sales.customers": "Voir les clients des ventes",
  "stock.read": "Voir le stock",
  "stock.promo": "Appliquer une remise",
  "arrivals.read": "Voir les arrivages",
  "boutiques.manage": "Gérer les boutiques et comptes Hiboutik",
  "network.customize": "Personnaliser le réseau (nom, logo)",
  "sync.run": "Lancer une synchronisation",
  "messages.read": "Lire les messages",
  "messages.send": "Envoyer des messages",
  "announcements.send": "Publier des annonces réseau",
  "tasks.create": "Créer des tâches",
  "tasks.complete": "Marquer une tâche faite",
  "tasks.validate": "Valider / refuser une tâche",
  "campaigns.read": "Voir les promos & campagnes",
  "campaigns.manage": "Gérer les promos & campagnes",
  "members.read": "Voir les accès",
  "members.manage": "Donner / retirer des accès",
  "reports.manage": "Gérer les rapports e-mail",
  "planning.read": "Voir le planning des équipes",
  "planning.manage": "Gérer le planning et les équipes",
  "it.manage": "Suivre les livraisons IT",
};

const ALL = [...PERMISSIONS];

export const ROLE_PERMISSIONS: Record<Role, readonly Permission[]> = {
  OWNER: ALL,
  ADMIN: ALL,
  MANAGER: [
    "dashboard.read",
    "sales.read",
    "sales.customers",
    "stock.read",
    "stock.promo",
    "arrivals.read",
    "sync.run",
    "messages.read",
    "messages.send",
    "tasks.create",
    "tasks.complete",
    "campaigns.read",
    // Campagnes de ses propres boutiques (pas celles de la tête de réseau).
    "campaigns.manage",
    // Un gérant peut ouvrir l'accès à son équipe (rôles inférieurs, sur ses
    // propres boutiques uniquement — voir les règles de délégation de l'API).
    "members.read",
    "members.manage",
    "planning.read",
    "planning.manage",
  ],
  STAFF: [
    "dashboard.read",
    "sales.read",
    "stock.read",
    "arrivals.read",
    "messages.read",
    "messages.send",
    "tasks.complete",
    "campaigns.read",
    "planning.read",
  ],
  VIEWER: ["dashboard.read", "sales.read", "stock.read", "arrivals.read", "campaigns.read", "planning.read"],
};

export interface PermissionOverrides {
  granted?: readonly string[] | null;
  revoked?: readonly string[] | null;
}

export function isPermission(p: string): p is Permission {
  return (PERMISSIONS as readonly string[]).includes(p);
}

/** Permissions effectives : défauts du rôle + ajouts − retraits. */
export function effectivePermissions(role: Role, overrides: PermissionOverrides = {}): Permission[] {
  const set = new Set<Permission>(ROLE_PERMISSIONS[role]);
  if (role !== "OWNER") {
    for (const p of overrides.granted ?? []) if (isPermission(p)) set.add(p);
    for (const p of overrides.revoked ?? []) if (isPermission(p)) set.delete(p);
  }
  return PERMISSIONS.filter((p) => set.has(p));
}

export function hasMinRole(role: Role, min: Role): boolean {
  return ROLE_HIERARCHY[role] >= ROLE_HIERARCHY[min];
}

export function isNetworkWide(role: Role): boolean {
  return NETWORK_WIDE_ROLES.includes(role);
}

/** Un membre ne peut attribuer que des rôles strictement inférieurs au sien (OWNER : tous sauf OWNER). */
export function assignableRoles(role: Role): Role[] {
  if (role === "OWNER") return ["ADMIN", "MANAGER", "STAFF", "VIEWER"];
  return ROLES.filter((r) => ROLE_HIERARCHY[r] < ROLE_HIERARCHY[role]);
}
